import React from 'react';
import PropTypes from 'prop-types';
import GaugeDial from './GaugeDial';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import {
    getMetricSourceForRequest,
    hasMetricSources,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import {
    getErrorMessageKey,
    getFormattedGaugeValue,
} from '../../utils/dataFormatUtils';

/* eslint-disable complexity */
function getMetricSource(metricRequests, metrics) {
    if (metricRequests.length !== 1 || metricRequests[0].metrics.length !== 1) {
        return {
            errorKey: 'invalidMetricRequests',
        };
    }

    const metricSource = getMetricSourceForRequest(metrics, metricRequests[0]);
    if (!metricSource) {
        return {
            errorKey: 'metricSourceNotFound',
        };
    }

    if (!allMetricSourcesAuthorized([metricSource])) {
        return {
            errorKey: 'metricSourceUnauthorized',
        };
    }

    if (!hasMetricSources([metricSource], true)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [metricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSource, metricRequest: metricRequests[0] };
}
/* eslint-enable complexity */

function getThresholdsFromConfig(configuration, thresholds) {
    const badThreshold = thresholds.find(
        (x) => x.id === configuration.badThresholdId
    );
    const okThreshold = thresholds.find(
        (x) => x.id === configuration.okThresholdId
    );
    const goodThreshold = thresholds.find(
        (x) => x.id === configuration.goodThresholdId
    );

    if (goodThreshold?.value === undefined) {
        return { thresholdErrorKey: 'invalidThresholdConfig' };
    }

    return { badThreshold, okThreshold, goodThreshold };
}

export default function GaugeDialWrapper(props) {
    const { thresholdErrorKey, ...thresholds } = getThresholdsFromConfig(
        props.configuration,
        props.thresholds
    );

    const { metricSource, metricRequest, metricSourceErrors, errorKey } =
        getMetricSource(props.metricRequests, props.metrics);

    const errorMessageKey = getErrorMessageKey(errorKey, thresholdErrorKey);
    if (errorMessageKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorMessageKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const metric = metricRequest.metrics[0];

    const value = getFormattedGaugeValue(
        metricSource.metricSeries[0]?.[metric],
        props.numberDecimalFormatter
    );

    return (
        <GaugeDial
            configuration={props.configuration}
            thresholds={thresholds}
            value={value}
        />
    );
}

GaugeDialWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    metricRequests: PropTypes.instanceOf(Array).isRequired,
    metrics: PropTypes.instanceOf(Array).isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
    thresholds: PropTypes.instanceOf(Array).isRequired,
};
